import IconBox from "assets/img/icon-box.svg?react"

import { FC } from "react"
import { useTranslation } from "react-i18next"
import cn from "classnames"

import ArticleDescription from "components/ArticleDescription/ArticleDescription"
import Checkbox from "components/Checkbox/Checkbox"
import { Flex, FlexItem } from "components/Flex"
import FlexibleContent from "components/FlexibleContent/FlexibleContent"
import { Grid, GridItem } from "components/Grid"
import Text from "components/Text/Text"
import { formatNumber } from "utils/utils"
import useArticleSelection, { IArticleSelectionItem } from "./useArticleSelection"

interface IArticleSelectionTableProps {
    articles: IArticleSelectionItem[]
    selection: ReturnType<typeof useArticleSelection>
    optionsGroupLabel?: string
}

const GRID_COLUMNS = "7% 59% 17% 17%"

/**
 * Article table with a selection column and the two per-article text options.
 *
 * Purely presentational — all state comes from `useArticleSelection`, so the configuration-text
 * dialog and the per-article invoice-position dialog share one implementation. Pass
 * `optionsGroupLabel` to render a heading spanning the two option columns.
 */
const ArticleSelectionTable: FC<IArticleSelectionTableProps> = ({
    articles,
    selection,
    optionsGroupLabel,
}) => {
    const { t } = useTranslation()
    const {
        value: { selectedArticles, mergeAssets, printFeatures },
        hasSelection,
        commonSelectionStatus,
        commonMergeAssetsStatus,
        commonPrintFeaturesStatus,
        setMergeAssets,
        setPrintFeatures,
        createSelectionClickHandler,
        handleCommonSelectionCheckboxChange,
        createOptionClickHandler,
        createCommonOptionChangeHandler,
    } = selection

    const optionLabelClassName = cn("text-center", hasSelection ? "color-grey-500" : "color-grey-400")

    return (
        <Flex flow="column" className="border-grey-100 round">
            {!!optionsGroupLabel &&
                <Grid columns={GRID_COLUMNS} className="border-grey-100-between bg-grey-50">
                    <div className="border-right-grey-100" />
                    <div className="border-right-grey-100" />
                    <GridItem column="span 2">
                        <Text type="pSmallest" className={cn(optionLabelClassName, "p-1")} bold>
                            {optionsGroupLabel}
                        </Text>
                    </GridItem>
                </Grid>
            }
            <Grid columns={GRID_COLUMNS} className="border-grey-100-between bg-grey-50">
                <Flex className="border-right-grey-100 p-1" justifyContent="center" alignItems="center">
                    <Checkbox
                        partiallyChecked={commonSelectionStatus === "partially"}
                        checked={commonSelectionStatus === "checked"}
                        onChange={handleCommonSelectionCheckboxChange}
                        testId="article-selection-bulk"
                    />
                </Flex>
                <div className="border-right-grey-100" />
                <Flex flow="column" justifyContent="center" alignItems="center" gap="1rem" className="border-right-grey-100 p-1">
                    <Text type="pSmallest" className={optionLabelClassName} bold>{t("textModal.mergeAssets")}</Text>
                    <Checkbox
                        partiallyChecked={commonMergeAssetsStatus === "partially"}
                        checked={commonMergeAssetsStatus === "checked"}
                        disabled={!hasSelection}
                        onChange={createCommonOptionChangeHandler(commonMergeAssetsStatus, setMergeAssets)}
                        testId="merge-assets-bulk"
                    />
                </Flex>
                <Flex flow="column" justifyContent="center" alignItems="center" gap="1rem" className="p-1">
                    <Text type="pSmallest" className={optionLabelClassName} bold>{t("textModal.printFeatures")}</Text>
                    <Checkbox
                        partiallyChecked={commonPrintFeaturesStatus === "partially"}
                        checked={commonPrintFeaturesStatus === "checked"}
                        disabled={!hasSelection}
                        onChange={createCommonOptionChangeHandler(commonPrintFeaturesStatus, setPrintFeatures)}
                        testId="print-features-bulk"
                    />
                </Flex>
            </Grid>
            {articles.map((item, index) => {
                const isSelected = !!selectedArticles[item.articleId]
                return (
                    <Grid columns={GRID_COLUMNS} className="border-grey-100-between" key={item.articleId}>
                        <Flex className="border-right-grey-100 p-1" justifyContent="center" alignItems="center">
                            <Checkbox
                                checked={isSelected}
                                onChange={createSelectionClickHandler(item.articleId)}
                                testId={`article-selection-${index}`}
                            />
                        </Flex>
                        <Flex flow="column" justifyContent="center" className={cn("border-right-grey-100 p-1", { "opacity-50": !isSelected })}>
                            <div className="relative">
                                <ArticleDescription
                                    articleDescription={item.articleDescription}
                                    articleId={item.articleId}
                                />
                            </div>
                            <Grid alignItems="center" columns="16% auto">
                                <Text type="pSmallest" className="pr-4">
                                    <FlexibleContent text={item.articleNumber} />
                                </Text>
                                <Text type="pSmallest">
                                    <Flex alignItems="center" gap="0.8rem">
                                        <FlexItem shrink={0}>
                                            <IconBox width="18" height="18" className="d-block" />
                                        </FlexItem>
                                        {formatNumber(item.assetsCount)}
                                    </Flex>
                                </Text>
                            </Grid>
                        </Flex>
                        <Flex className="border-right-grey-100 p-1" justifyContent="center" alignItems="center">
                            <Checkbox
                                checked={isSelected && (mergeAssets[item.articleId] ?? false)}
                                disabled={!isSelected}
                                onChange={createOptionClickHandler(item.articleId, setMergeAssets)}
                                testId={`merge-assets-${index}`}
                            />
                        </Flex>
                        <Flex justifyContent="center" className="p-1" alignItems="center">
                            <Checkbox
                                checked={isSelected && (printFeatures[item.articleId] ?? false)}
                                disabled={!isSelected}
                                onChange={createOptionClickHandler(item.articleId, setPrintFeatures)}
                                testId={`print-features-${index}`}
                            />
                        </Flex>
                    </Grid>
                )
            })}
        </Flex>
    )
}

export default ArticleSelectionTable
